"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";

/* Path dihitung dari app/layanan/asuransi/ - tempat berkas INI berada.
     ../../components, ../../i18n  -> naik dua tingkat ke app/
     ./asuransi                    -> tetangga di folder yang sama */
import Footer from "../../components/Footer";
import { useLanguage } from "../../i18n/LanguageProvider";
import {
  KONTAK,
  PESAN_WA,
  TERAKHIR_DIPERBARUI,
  logoDari,
  mitraTampil,
  namaTampil,
} from "./asuransi";

/* ============================================================
   MITRA ASURANSI - /layanan/asuransi

   Sebelum berkas ini ada, alamat ini jatuh ke halaman 404 "Sedang
   Dalam Proses Pengembangan" - padahal sudah ditunjuk menu Header.

   ============================================================
   KENAPA HALAMAN INI PUNYA KOTAK PENCARIAN, SEMENTARA HALAMAN
   LAYANAN LAIN TIDAK

   Isinya 117 nama. Halaman lain memuat tiga sampai sepuluh.

   Orang yang membuka halaman ini datang dengan SATU pertanyaan -
   "asuransi saya ada atau tidak" - dan sudah tahu nama yang
   dicarinya. Menyuruhnya menggulir 117 baris untuk menjawab
   pertanyaan ya/tidak adalah pekerjaan yang tidak perlu, apalagi
   di ponsel.

   Pencariannya bekerja di sisi klien, tanpa menghubungi apa pun:
   seluruh daftar sudah ada di HTML sejak halaman dibuka. Jadi ia
   tetap berfungsi di jaringan yang lambat, dan mesin pencari tetap
   membaca seluruh nama.

   ============================================================
   PENCARIANNYA MENCOCOKKAN NAMA ASLI, BUKAN HANYA YANG TAMPIL

   Yang tampil di layar sudah dirapikan - "ACA Total" ditampilkan
   sebagai "ACA". Tapi kartu peserta orang bisa saja tertulis versi
   panjangnya, dan sebagian orang menyalin persis dari kartunya.

   Jadi pencocokannya dilakukan pada KEDUANYA. Mengetik "aca" atau
   "aca total" sama-sama menemukan entri yang benar.

   Tanda baca ikut diabaikan: "PT. BANK BTN" ketemu dengan mengetik
   "pt bank btn" tanpa titik.

   ============================================================
   >>> JANGAN TULIS className SEBAGAI STRING MULTI-BARIS. <<<
   Proyek ini berakhiran baris CRLF. String class yang dipecah
   beberapa baris ikut membawa \r\n ke dalam nilai atribut dan
   memicu hydration error. Lihat catatan di Facility.jsx.
   ============================================================ */

const LEBAR_HALAMAN = 1140;

/* Halaman induk /layanan sudah ada, jadi remah rotinya boleh jadi
   tautan. Sama dengan halaman layanan lain. */
const TAUTKAN_INDUK = true;

const TEKS = {
  eyebrow: { id: "Layanan Kami", en: "Our Services" },
  judul: { id: "Mitra Asuransi", en: "Insurance Partners" },
  sub: {
    id: "Daftar perusahaan asuransi dan pengelola penjaminan yang bekerja sama dengan RS Jakarta Medical Center.",
    en: "Insurers and third-party administrators that work with Jakarta Medical Center Hospital.",
  },

  /* Kalimat terpenting di halaman ini, dan sengaja ditaruh di atas
     daftar - bukan sebagai catatan kaki yang tidak terbaca.

     Nama yang tercantum TIDAK berarti semua tindakan ditanggung.
     Itu bergantung pada jenis polis, plafon, dan rujukan. Orang
     yang datang dengan anggapan sebaliknya akan berhadapan dengan
     tagihan yang tidak disangka - dan itu terjadi di kasir, saat
     sudah terlambat. */
  peringatan: {
    id: "Tercantum di daftar ini berarti RS JMC bekerja sama dengan penjamin tersebut - bukan berarti seluruh tindakan otomatis ditanggung. Cakupan bergantung pada jenis polis, plafon, dan ketentuan masing-masing penjamin. Pastikan dulu ke Customer Service sebelum datang.",
    en: "Being listed here means RS JMC works with that provider - it does not mean every procedure is automatically covered. Coverage depends on your policy type, limits and the provider's own terms. Please confirm with customer service before your visit.",
  },

  cari: { id: "Cari nama asuransi", en: "Search for an insurer" },
  cariPetunjuk: {
    id: "Ketik sebagian namanya, misalnya \u201cprudential\u201d atau \u201cbni\u201d.",
    en: "Type part of the name, for example \u201cprudential\u201d or \u201cbni\u201d.",
  },
  bersihkan: { id: "Hapus pencarian", en: "Clear search" },

  jumlah: { id: "penjamin bekerja sama", en: "partners" },
  hasil: { id: "ditemukan", en: "found" },
  diperbarui: { id: "Diperbarui", en: "Updated" },

  memuat: { id: "Memuat logo mitra\u2026", en: "Loading partner logos\u2026" },

  /* Judul untuk kelompok bawah. Sengaja TIDAK berbunyi "Belum ada
     logo" - itu kalimat untuk tim internal. Bagi pasien, kelompok
     ini sama sahnya dengan yang di atas; yang membedakan hanya
     ketersediaan berkas gambar, dan itu bukan urusan mereka. */
  lainnya: { id: "Mitra lainnya", en: "Other partners" },
  lainnyaIsi: {
    id: "Penjamin berikut juga bekerja sama dengan RS JMC.",
    en: "The following providers also work with RS JMC.",
  },

  kosongJudul: { id: "Tidak ditemukan", en: "No matches" },
  kosongIsi: {
    id: "Nama itu tidak ada di daftar. Daftar mitra bisa berubah sewaktu-waktu, jadi sebaiknya tetap tanyakan ke Customer Service sebelum menyimpulkan.",
    en: "That name is not on the list. Partner lists change from time to time, so it is still worth asking customer service before concluding.",
  },

  bantuanJudul: { id: "Asuransi Anda tidak ada di daftar?", en: "Insurer not on the list?" },
  bantuanIsi: {
    id: "Atau ingin memastikan tindakan yang Anda butuhkan ditanggung? Hubungi Customer Service dengan menyebutkan nama penjamin, nomor polis, dan layanan yang dituju.",
    en: "Or want to check whether the treatment you need is covered? Contact customer service with your provider's name, policy number and the service you need.",
  },
  chatWa: { id: "Chat lewat WhatsApp", en: "Chat on WhatsApp" },
  teleponSekarang: { id: "Telepon sekarang", en: "Call now" },
};

function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

/* Membuang tanda baca dan merapikan spasi, supaya "PT. BANK BTN"
   dan "pt bank btn" dianggap sama. */
function normal(s) {
  return String(s)
    .toLowerCase()
    .replace(/[.,/()\-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export default function AsuransiContent() {
  const { lang, t } = useLanguage();
  const [cari, setCari] = useState("");

  /* Dihitung sekali, bukan tiap ketikan. 117 entri memang tidak
     berat, tapi ini juga yang menyimpan bentuk normalnya sehingga
     tidak dihitung ulang untuk tiap huruf yang diketik. */
  const daftar = useMemo(() => {
    return mitraTampil().map((m) => {
      const tampil = namaTampil(m.nama);
      return {
        asli: m.nama,
        tampil,
        /* Dua bentuk digabung: yang tampil DAN yang asli. Lihat
           catatan pencarian di kepala berkas. */
        kunci: `${normal(tampil)} ${normal(m.nama)}`,
        logo: logoDari(m),
      };
    });
  }, []);

  const hasil = useMemo(() => {
    const q = normal(cari);
    if (!q) return daftar;
    /* Tiap kata dicari terpisah, jadi "bni life" tetap ketemu
       walaupun urutan katanya berbeda di nama aslinya. */
    const kata = q.split(" ").filter(Boolean);
    return daftar.filter((d) => kata.every((k) => d.kunci.includes(k)));
  }, [cari, daftar]);

  /* ============================================================
     PEMERIKSAAN LOGO

     Halaman ini menaruh mitra tanpa logo di bagian bawah. Masalahnya,
     ADA atau TIDAKNYA sebuah berkas logo baru diketahui setelah
     browser mencoba memuatnya - jadi urutannya tidak bisa ditentukan
     dari data saja.

     Kalau kartunya dirender lebih dulu lalu yang gagal dipindah ke
     bawah, halaman akan terlihat melompat-lompat beberapa detik
     pertama - dan pada 117 kartu, itu sangat terlihat.

     Jadi semua logo diperiksa DULU di sini, baru grid-nya digambar
     sekali dalam urutan final. Pemeriksaannya berjalan berbarengan,
     bukan satu per satu.

     BATAS 4 DETIK. Kalau ada berkas yang menggantung (jaringan
     lambat, peladen tidak menjawab), halaman tidak boleh ikut
     menggantung selamanya. Lewat 4 detik, yang belum menjawab
     dianggap tidak ada dan masuk kelompok bawah - kalau ternyata
     berkasnya ada, ia akan muncul normal saat halaman dibuka lagi
     karena sudah tersimpan di cache browser.
     ============================================================ */
  const [logoAda, setLogoAda] = useState(null);

  useEffect(() => {
    let batal = false;

    /* Path yang sama bisa dipakai lebih dari satu entri (mis. dua
       nama berbeda yang menunjuk berkas yang sama). Diperiksa sekali
       saja per path. */
    const path = [...new Set(daftar.map((d) => d.logo).filter(Boolean))];

    if (path.length === 0) {
      setLogoAda(new Set());
      return;
    }

    const ada = new Set();
    let sisa = path.length;

    const selesai = () => {
      if (batal) return;
      setLogoAda(ada);
    };

    const tenggat = setTimeout(selesai, 4000);

    const kurangi = () => {
      sisa -= 1;
      if (sisa === 0) {
        clearTimeout(tenggat);
        selesai();
      }
    };

    for (const src of path) {
      const img = new window.Image();
      img.onload = () => {
        ada.add(src);
        kurangi();
      };
      img.onerror = kurangi;
      img.src = src;
    }

    return () => {
      batal = true;
      clearTimeout(tenggat);
    };
  }, [daftar]);

  /* Dua kelompok, tanpa pembagian per huruf. Di dalam tiap kelompok
     tetap urut abjad - 117 nama tanpa urutan apa pun tidak bisa
     ditelusuri mata.

     logoAda === null berarti pemeriksaan belum selesai. Selama itu
     kedua kelompok sengaja kosong; yang tampil hanya penanda memuat. */
  const { berlogo, tanpaLogo } = useMemo(() => {
    if (logoAda === null) return { berlogo: [], tanpaLogo: [] };

    const urut = (a, b) => a.tampil.localeCompare(b.tampil, "id");
    const punya = [];
    const tidak = [];

    for (const d of hasil) {
      (d.logo && logoAda.has(d.logo) ? punya : tidak).push(d);
    }

    return { berlogo: punya.sort(urut), tanpaLogo: tidak.sort(urut) };
  }, [hasil, logoAda]);

  const utama = KONTAK.find((n) => n.utama) ?? KONTAK[0];

  function tautanNomor(n) {
    if (!n.wa) return `tel:${n.tel}`;
    return `https://wa.me/${n.wa}?text=${encodeURIComponent(pilihTeks(PESAN_WA, lang))}`;
  }

  return (
    <div className="w-full bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BREADCRUMB ---------- */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div style={{ maxWidth: 1600, margin: "0 auto", padding: "12px 16px" }}>
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                  {t("header.nav.home")}
                </Link>
                <Pemisah />
              </li>
              <li className="flex items-center">
                {TAUTKAN_INDUK ? (
                  <Link href="/layanan" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                    {t("header.menu.layanan.label")}
                  </Link>
                ) : (
                  <span className="text-[var(--c-muted)]">{t("header.menu.layanan.label")}</span>
                )}
                <Pemisah />
              </li>
              <li className="flex items-center">
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {pilihTeks(TEKS.judul, lang)}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* ---------- KEPALA ----------
         Satu kolom, bukan dua seperti halaman layanan lain. Tidak
         ada foto yang pantas menemani daftar nama perusahaan, dan
         kolom teks selebar penuh membuat peringatan di bawahnya
         terbaca sebagai bagian dari pembuka - bukan sisipan. */}
      <div className="bg-[var(--c-tint)]">
        <div style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "48px 16px 44px" }}>
          <p className="font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)]" style={{ fontSize: 11.5 }}>
            {pilihTeks(TEKS.eyebrow, lang)}
          </p>

          <h1 className="text-balance font-bold leading-[1.08] tracking-tight text-[var(--c-ink)]" style={{ marginTop: 10, fontSize: "clamp(30px, 4.4vw, 44px)" }}>
            {pilihTeks(TEKS.judul, lang)}
          </h1>

          <p className="text-[var(--c-body)]" style={{ marginTop: 14, maxWidth: 640, fontSize: 15.5, lineHeight: 1.7 }}>
            {pilihTeks(TEKS.sub, lang)}
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginTop: 18 }}>
            <span className="font-bold text-white" style={{ backgroundColor: "#0f6a3b", borderRadius: 999, padding: "6px 16px", fontSize: 14 }}>
              {daftar.length} {pilihTeks(TEKS.jumlah, lang)}
            </span>

            {/* Tanggal hanya muncul kalau diisi. Daftar penjamin tanpa
               tanggal lebih baik daripada tanggal yang sudah basi -
               lihat catatan di asuransi.js. */}
            {TERAKHIR_DIPERBARUI && (
              <span className="text-[var(--c-muted)]" style={{ fontSize: 13.5 }}>
                {pilihTeks(TEKS.diperbarui, lang)} {TERAKHIR_DIPERBARUI}
              </span>
            )}
          </div>

          {/* PERINGATAN CAKUPAN - di atas daftar, bukan di bawahnya.
             Alasannya ada di TEKS.peringatan. Jangan dipindah ke
             kaki halaman: yang membacanya di sana hanya orang yang
             sudah menggulir sampai habis, dan mereka justru bukan
             yang paling perlu membacanya. */}
          <div className="border-l-4 border-[var(--c-brand-mid)] bg-[var(--c-surface)]" style={{ marginTop: 22, maxWidth: 760, borderRadius: 10, padding: "14px 16px" }}>
            <p className="text-slate-700" style={{ fontSize: 14, lineHeight: 1.7 }}>
              {pilihTeks(TEKS.peringatan, lang)}
            </p>
          </div>
        </div>
      </div>

      <section style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "36px 16px 64px" }}>

        {/* ---------- PENCARIAN ---------- */}
        <div style={{ maxWidth: 520 }}>
          <label htmlFor="cari-asuransi" className="block font-semibold text-[var(--c-ink)]" style={{ fontSize: 14 }}>
            {pilihTeks(TEKS.cari, lang)}
          </label>

          <div style={{ position: "relative", marginTop: 8 }}>
            <span aria-hidden="true" className="text-[var(--c-faint)]" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", display: "flex" }}>
              <IkonCari />
            </span>

            <input
              id="cari-asuransi"
              type="search"
              value={cari}
              onChange={(e) => setCari(e.target.value)}
              placeholder={pilihTeks(TEKS.cariPetunjuk, lang)}
              className="w-full border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink)] transition-colors placeholder:text-slate-400 focus:border-[var(--c-brand)] focus:outline-none focus:ring-2 focus:ring-[var(--c-brand-line)]"
              style={{ borderRadius: 12, padding: "13px 44px 13px 44px", fontSize: 15 }}
            />

            {cari && (
              <button
                type="button"
                onClick={() => setCari("")}
                aria-label={pilihTeks(TEKS.bersihkan, lang)}
                className="text-[var(--c-faint)] transition-colors hover:text-[var(--c-brand)]"
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", display: "flex", padding: 6 }}
              >
                <IkonSilang />
              </button>
            )}
          </div>

          {/* aria-live: pembaca layar mengumumkan jumlah hasil tiap
             kali pencarian berubah. Tanpa ini, pengguna tunanetra
             mengetik tanpa tahu apakah ada yang cocok. */}
          <p aria-live="polite" className="text-[var(--c-muted)]" style={{ marginTop: 8, fontSize: 13 }}>
            {cari ? `${hasil.length} ${pilihTeks(TEKS.hasil, lang)}` : ""}
          </p>
        </div>

        {/* ---------- DAFTAR ---------- */}
        {logoAda === null ? (
          /* Penanda memuat. minHeight menahan tinggi halaman supaya
             kaki halaman tidak melompat naik-turun saat grid muncul. */
          <div style={{ marginTop: 32, minHeight: 220, display: "flex", alignItems: "flex-start" }}>
            <p className="text-[var(--c-muted)]" style={{ fontSize: 14 }}>
              {pilihTeks(TEKS.memuat, lang)}
            </p>
          </div>
        ) : hasil.length === 0 ? (
          <div className="border border-[var(--c-line)] bg-[var(--c-surface-2)]" style={{ marginTop: 24, borderRadius: 14, padding: "28px 22px", maxWidth: 640 }}>
            <p className="font-bold text-[var(--c-ink)]" style={{ fontSize: 16.5 }}>
              {pilihTeks(TEKS.kosongJudul, lang)}
            </p>
            <p className="text-[var(--c-body)]" style={{ marginTop: 8, fontSize: 14.5, lineHeight: 1.7 }}>
              {pilihTeks(TEKS.kosongIsi, lang)}
            </p>
          </div>
        ) : (
          <div style={{ marginTop: 30 }}>

            {/* ---------- DINDING LOGO ----------
               Satu grid untuk semua, tanpa pemisah huruf.

               minmax(168px, 1fr): kolomnya mengikuti lebar layar
               sendiri - 2 di ponsel sampai 6 di layar lebar - tanpa
               perlu menulis breakpoint satu per satu.

               Tinggi kartu DIKUNCI 104px dan logo dibatasi 44px.
               Logo mitra datang dengan rasio yang jauh berbeda:
               Allianz sangat memanjang, BPJS hampir bujur sangkar.
               Tanpa tinggi tetap, barisnya bergelombang dan dinding
               logonya terlihat berantakan - dan itu justru yang
               paling cepat terbaca sebagai "situs belum jadi". */}
            {berlogo.length > 0 && (
              <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fill, minmax(168px, 1fr))" }}>
                {berlogo.map((d) => (
                  <KartuLogo key={d.asli} mitra={d} />
                ))}
              </div>
            )}

            {/* ---------- MITRA LAINNYA ----------
               Yang belum punya berkas logo. Ditaruh di bawah, dan
               dibedakan bentuknya - bukan kotak besar berisi teks
               kecil di tengah, melainkan kartu nama yang rapat.

               Bentuk yang berbeda itu disengaja. Kotak seukuran
               kartu logo tapi isinya teks akan terbaca sebagai
               "gambarnya gagal dimuat". Kartu nama yang rapat
               terbaca sebagai daftar - yang memang isinya. */}
            {tanpaLogo.length > 0 && (
              <div style={{ marginTop: berlogo.length > 0 ? 44 : 0 }}>
                {berlogo.length > 0 && (
                  <div className="border-t border-[var(--c-line)]" style={{ paddingTop: 26 }}>
                    <h2 className="font-bold text-[var(--c-ink)]" style={{ fontSize: 16 }}>
                      {pilihTeks(TEKS.lainnya, lang)}
                    </h2>
                    <p className="text-[var(--c-muted)]" style={{ marginTop: 4, fontSize: 13.5 }}>
                      {pilihTeks(TEKS.lainnyaIsi, lang)}
                    </p>
                  </div>
                )}

                <div style={{ marginTop: 16, display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
                  {tanpaLogo.map((d) => (
                    <KartuNama key={d.asli} mitra={d} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---------- BANTUAN ----------
           Halaman ini harus berakhir dengan nomor, bukan dengan
           daftar. Daftar tidak menjawab pertanyaan yang sebenarnya -
           lihat catatan KONTAK di asuransi.js. */}
        <div className="border border-[var(--c-line)] bg-[var(--c-tint)]" style={{ marginTop: 48, borderRadius: 16, padding: "26px 22px" }}>
          <h2 className="font-bold text-[var(--c-ink)]" style={{ fontSize: 19 }}>
            {pilihTeks(TEKS.bantuanJudul, lang)}
          </h2>
          <p className="text-[var(--c-body)]" style={{ marginTop: 8, maxWidth: 640, fontSize: 14.5, lineHeight: 1.7 }}>
            {pilihTeks(TEKS.bantuanIsi, lang)}
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 20 }}>
            <a
              href={tautanNomor(utama)}
              target={utama.wa ? "_blank" : undefined}
              rel={utama.wa ? "noopener noreferrer" : undefined}
              className="inline-flex items-center gap-3 rounded-[12px] bg-[var(--c-brand)] font-bold text-white shadow-sm transition-colors hover:bg-[var(--c-brand-strong)]"
              style={{ padding: "14px 24px", fontSize: 16 }}
            >
              {utama.wa ? <IkonWhatsApp /> : <IkonTelepon />}
              {utama.tampil}
            </a>

            {KONTAK.filter((n) => !n.utama).map((n) => (
              <a
                key={n.tel}
                href={`tel:${n.tel}`}
                className="inline-flex items-center gap-2 rounded-[12px] border border-[var(--c-brand-line)] bg-[var(--c-surface)] font-bold text-[var(--c-brand)] transition-colors hover:bg-[var(--c-tint-2)]"
                style={{ padding: "14px 20px", fontSize: 15 }}
              >
                <IkonTelepon />
                {n.tampil}
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ============================================================
   NAMA BERKAS DI LAYAR - HANYA SAAT PENGEMBANGAN

   Kartu tanpa logo menuliskan nama berkas yang sedang ditunggu,
   supaya Anda bisa membaca nama yang kurang langsung di layar dan
   menyimpannya - tanpa membuka berkas kode untuk mencocokkan.

   Tapi itu catatan kerja, bukan informasi untuk pasien. Di situs
   yang tayang, "bintang.png" di bawah nama penjamin membuat halaman
   terlihat setengah jadi.

   Jadi hanya muncul saat `npm run dev`. Setelah `npm run build`,
   nilainya false dan blok itu hilang sendiri - tidak perlu diingat
   untuk dihapus.
   ============================================================ */
const TAMPILKAN_NAMA_BERKAS = process.env.NODE_ENV !== "production";

/* ============================================================
   KARTU LOGO

   Dipakai HANYA untuk mitra yang berkas logonya sudah dipastikan
   ada - pemeriksaannya terjadi di komponen induk. Jadi kartu ini
   tidak butuh penanganan gagal-muat sendiri.

   >>> KENAPA <img>, BUKAN next/image <<<

   next/image butuh lebar dan tinggi yang diketahui di muka, atau
   `fill` dengan induk berposisi relatif - sementara logo mitra
   datang dengan ukuran dan rasio yang berbeda-beda, dan tidak ada
   satu pun angka yang benar untuk semuanya.

   Konsekuensinya logo tidak dioptimalkan otomatis. Karena itu
   simpan logo dalam ukuran yang masuk akal (tinggi ~200px), jangan
   unggah PNG 2000px - di halaman ini ada lebih dari seratus berkas,
   dan bobotnya menumpuk.
   ============================================================ */
function KartuLogo({ mitra }) {
  return (
    <div
      className="group border border-[var(--c-line)] bg-white transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--c-brand-line)] hover:shadow-[0_1px_2px_rgba(15,106,59,0.06),0_10px_22px_-14px_rgba(15,106,59,0.28)] motion-reduce:transform-none"
      style={{ borderRadius: 14, height: 104, display: "flex", alignItems: "center", justifyContent: "center", padding: "14px 18px" }}
      /* Nama lengkap terbaca saat kursor disorot. Banyak logo tidak
         memuat nama perusahaannya dalam bentuk yang terbaca. */
      title={mitra.tampil}
    >
      {/* alt berisi nama mitra, bukan kosong: kartu ini satu-satunya
         tempat nama itu muncul. Dikosongkan, pengguna pembaca layar
         tidak mendengar apa pun di sini.

         grayscale yang hilang saat disorot: 100+ logo berwarna
         berjajar menghasilkan dinding yang ramai dan sulit
         ditelusuri. Diredam sedikit, matanya bisa memindai bentuk
         dulu - dan warnanya kembali penuh saat satu kartu
         diperhatikan. */}
      <img
        src={mitra.logo}
        alt={mitra.tampil}
        loading="lazy"
        decoding="async"
        className="opacity-90 grayscale-[35%] transition duration-200 group-hover:opacity-100 group-hover:grayscale-0"
        style={{ maxWidth: "100%", maxHeight: 44, width: "auto", height: "auto", objectFit: "contain" }}
      />
    </div>
  );
}

/* ============================================================
   KARTU NAMA

   Untuk mitra yang berkas logonya belum ada. Bentuknya sengaja
   BERBEDA dari kartu logo - lebih pendek, rata kiri, dengan
   penanda titik di depan seperti butir daftar.

   Kalau bentuknya disamakan, kotak besar berisi teks kecil di
   tengah akan terbaca sebagai "gambarnya gagal dimuat". Bentuk
   yang jelas berbeda terbaca sebagai daftar - dan itu memang
   isinya.
   ============================================================ */
function KartuNama({ mitra }) {
  return (
    <div
      className="border border-[var(--c-line)] bg-[var(--c-surface-2)]"
      style={{ borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 10 }}
    >
      <span aria-hidden="true" style={{ marginTop: 7, width: 5, height: 5, flex: "0 0 auto", borderRadius: 999, backgroundColor: "#bfe3cf" }} />

      <div style={{ minWidth: 0 }}>
        <p className="font-semibold text-[var(--c-ink)]" style={{ fontSize: 13.5, lineHeight: 1.45 }}>
          {mitra.tampil}
        </p>

        {TAMPILKAN_NAMA_BERKAS && mitra.logo && (
          <code className="text-[var(--c-muted)]" style={{ display: "block", marginTop: 4, fontSize: 10, wordBreak: "break-all" }}>
            {mitra.logo.replace("/images/asuransi/", "")}
          </code>
        )}
      </div>
    </div>
  );
}

/* Ikon ditulis sebagai komponen kecil supaya dipakai berulang tanpa
   menyalin path SVG-nya. aria-hidden karena maknanya sudah
   disampaikan tulisan di sebelahnya. */
function IkonCari() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18 }}>
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="m16 16 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IkonSilang() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 16, height: 16 }}>
      <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IkonTelepon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18, flex: "0 0 auto" }}>
      <path d="M4.5 3.5h3l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 2.5 5.7 2 2 0 0 1 4.5 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function IkonWhatsApp() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18, flex: "0 0 auto" }}>
      <path d="M3.5 20.5l1.3-4.2A8.2 8.2 0 1 1 8 19.3l-4.5 1.2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M9 8.5c.3 2.5 2 4.2 4.5 4.5l.8-1.3 2 .8v1.3c0 .6-.5 1.1-1.1 1a7.8 7.8 0 0 1-7.2-7.2c0-.6.4-1.1 1-1.1h1.3l.8 2L9 8.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

function Pemisah() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
